import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../config";
import {
    Building2,
    GitBranch,
    Users,
    TrendingUp,
    ArrowUp,
    ArrowDown,
    BarChart3,
    Activity,
    Target,
    Calendar,
    Clock,
    ChevronRight,
    Loader2,
    AlertCircle,
    CheckCircle,
    XCircle,
    School,
    Eye,
    RefreshCw
} from "lucide-react";

/**
 * Management Dashboard - For users with the "management" role
 * Displays aggregated statistics across all schools/branches that this management user can access
 * 
 * The management role user sees:
 * - Their own school (which is the group parent)
 * - All branch schools linked via parent_school_id
 */
const ManagementDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [branchData, setBranchData] = useState([]);
    const [overviewStats, setOverviewStats] = useState(null);
    const [currentSchool, setCurrentSchool] = useState(null);

    // API base URL imported from config

    // API helper function
    const apiCall = useCallback(async (endpoint, options = {}) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP ${response.status}`);
        }

        return response.json();
    }, [API_BASE_URL]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Get current user info to determine the school context
            const schoolId = localStorage.getItem('school_id');
            const userRole = localStorage.getItem('user_role');

            console.log('Fetching management data for school:', schoolId, 'role:', userRole);

            // Fetch dashboard stats for current school (Head Office) AND branch overview
            const [localStats, branchStats] = await Promise.all([
                apiCall('/admin/dashboard/comprehensive-stats').catch(() => null),
                apiCall('/admin/dashboard/branch-overview').catch(() => ({ branches: [] }))
            ]);

            if (localStats) {
                // Process data from the current school's comprehensive stats
                setCurrentSchool({
                    id: schoolId,
                    name: localStats.schoolName || 'Head Office', // Default to Head Office if name missing
                    role: userRole
                });

                // 1. Prepare Branch List
                // We typically include the Head Office as the first "branch" in the list
                const headOffice = {
                    id: schoolId,
                    name: 'Head Office (Main)',
                    status: 'active', // Assumed active if we are logged in
                    students: localStats.userStats?.studentCount || 0,
                    teachers: localStats.userStats?.teacherCount || 0,
                    attendance: localStats.systemStats?.attendanceRate || 0,
                    performance: localStats.academicStats?.competencyCompletionRate || 0,
                    trend: localStats.userStats?.userGrowthRate > 0 ? 'up' : 'stable'
                };

                // Map backend branch data to UI format
                // Backend returns: { id, name, status, student_count, teacher_count, attendance_rate, performance_rate }
                const mappedBranches = (branchStats.branches || []).map(b => ({
                    id: b.id,
                    name: b.name,
                    status: b.status || 'active',
                    students: b.student_count || 0,
                    teachers: b.teacher_count || 0,
                    attendance: b.attendance_rate || 0,
                    performance: b.performance_rate || 0,
                    trend: 'stable' // We don't have trend data for branches yet
                }));

                const allBranches = [headOffice, ...mappedBranches];
                setBranchData(allBranches);

                // 2. Calculate Aggregated Stats
                const totalStudents = allBranches.reduce((sum, b) => sum + b.students, 0);
                const totalTeachers = allBranches.reduce((sum, b) => sum + b.teachers, 0);

                // Weighted average for percentages based on student count (or simple average if 0 users)
                // Using simple average for now to avoid division by zero complexity
                const avgAttendance = Math.round(allBranches.reduce((sum, b) => sum + b.attendance, 0) / allBranches.length);
                const avgPerformance = Math.round(allBranches.reduce((sum, b) => sum + b.performance, 0) / allBranches.length);

                setOverviewStats({
                    totalStudents,
                    totalTeachers,
                    avgAttendance,
                    avgPerformance,
                    totalBranches: allBranches.length - 1, // Exclude Head Office from "Branches" count? Or include? Let's say Total Campuses
                    activeBranches: allBranches.filter(b => b.status === 'active').length,
                    totalUsers: totalStudents + totalTeachers + (localStats.userStats?.adminCount || 0) + (localStats.userStats?.parentCount || 0), // Approx
                    userGrowthRate: localStats.userStats?.userGrowthRate || 0 // Use HO growth as proxy for now
                });

            } else {
                // Fallback with empty data
                setOverviewStats({
                    totalStudents: 0,
                    totalTeachers: 0,
                    avgAttendance: 0,
                    avgPerformance: 0,
                    totalBranches: 0,
                    activeBranches: 0,
                    totalUsers: 0,
                    userGrowthRate: 0
                });
                setBranchData([]);
            }

        } catch (err) {
            console.error('Failed to load management data:', err);
            setError('Failed to load branch data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [apiCall]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getStatusBadge = (status) => {
        const config = {
            active: { color: 'bg-green-100 text-green-700', icon: <CheckCircle size={14} /> },
            trial: { color: 'bg-yellow-100 text-yellow-700', icon: <Clock size={14} /> },
            inactive: { color: 'bg-gray-100 text-gray-600', icon: <XCircle size={14} /> }
        };
        const badge = config[status] || config.inactive;
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.icon}
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const getTrendIcon = (trend) => {
        if (trend === 'up') return <ArrowUp size={16} className="text-green-500" />;
        if (trend === 'down') return <ArrowDown size={16} className="text-red-500" />;
        return <Activity size={16} className="text-gray-400" />;
    };

    if (loading) {
        return (
            <>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <Loader2 className="w-12 h-12 text-[#D67C2C] animate-spin mx-auto mb-4" />
                            <p className="text-gray-600">Loading branch data...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
                            <p className="text-lg font-medium text-red-600">{error}</p>
                            <button
                                onClick={fetchData}
                                className="mt-4 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors flex items-center gap-2 mx-auto"
                            >
                                <RefreshCw size={16} />
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

                    {/* Header */}
                    <div className="pt-8 pb-8 text-center relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-gradient-to-br from-purple-500 to-purple-600">
                                <GitBranch className="w-8 h-8 text-white" />
                            </div>
                            <h1 className="text-4xl font-bold text-[#1D3469] mb-4">
                                Branch Management Overview
                            </h1>
                            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                                Monitor and compare performance across all school branches in your organization
                            </p>
                            {currentSchool && (
                                <p className="text-sm text-gray-500 mt-2">
                                    Logged in as: <span className="font-medium text-purple-600">{currentSchool.role}</span>
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Overview Stats */}
                    {overviewStats && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all">
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Total Students</p>
                                            <p className="text-3xl font-bold text-purple-600 mt-1">{overviewStats.totalStudents.toLocaleString()}</p>
                                            <p className="text-sm text-gray-500 mt-2">Across all branches</p>
                                        </div>
                                        <div className="p-3 rounded-full bg-purple-100">
                                            <Users className="w-6 h-6 text-purple-600" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all">
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Total Teachers</p>
                                            <p className="text-3xl font-bold text-blue-600 mt-1">{overviewStats.totalTeachers}</p>
                                            <p className="text-sm text-gray-500 mt-2">Active educators</p>
                                        </div>
                                        <div className="p-3 rounded-full bg-blue-100">
                                            <School className="w-6 h-6 text-blue-600" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all">
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Task Completion</p>
                                            <p className="text-3xl font-bold text-green-600 mt-1">{overviewStats.avgAttendance}%</p>
                                            <div className="flex items-center gap-1 mt-2">
                                                {overviewStats.userGrowthRate > 0 && (
                                                    <>
                                                        <ArrowUp size={14} className="text-green-500" />
                                                        <span className="text-sm text-green-600">+{overviewStats.userGrowthRate}%</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="p-3 rounded-full bg-green-100">
                                            <Calendar className="w-6 h-6 text-green-600" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all">
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Avg Performance</p>
                                            <p className="text-3xl font-bold text-[#D67C2C] mt-1">{overviewStats.avgPerformance}%</p>
                                            <p className="text-sm text-gray-500 mt-2">Competency score</p>
                                        </div>
                                        <div className="p-3 rounded-full bg-orange-100">
                                            <Target className="w-6 h-6 text-[#D67C2C]" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Branch Cards */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
                                    <Building2 className="w-5 h-5 text-purple-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900">Branch Performance</h2>
                            </div>
                            <button
                                onClick={fetchData}
                                className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            >
                                <RefreshCw size={18} />
                            </button>
                        </div>

                        {branchData.length === 0 ? (
                            <div className="bg-gray-50 rounded-xl p-8 text-center">
                                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">No branch data available yet.</p>
                                <p className="text-sm text-gray-400 mt-2">Branch data will appear here once schools are created.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {branchData.map((branch) => (
                                    <div
                                        key={branch.id}
                                        className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg hover:border-purple-200 transition-all cursor-pointer group"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                                    <Building2 size={18} className="text-purple-500" />
                                                    {branch.name}
                                                </h3>
                                                <div className="mt-2">
                                                    {getStatusBadge(branch.status)}
                                                </div>
                                            </div>
                                            <button className="p-2 bg-gray-50 rounded-lg group-hover:bg-purple-50 transition-colors">
                                                <Eye size={18} className="text-gray-400 group-hover:text-purple-500" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div className="bg-gray-50 rounded-lg p-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Users size={14} className="text-purple-500" />
                                                    <span className="text-xs text-gray-500">Students</span>
                                                </div>
                                                <p className="text-xl font-bold text-gray-800">{branch.students}</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <School size={14} className="text-blue-500" />
                                                    <span className="text-xs text-gray-500">Teachers</span>
                                                </div>
                                                <p className="text-xl font-bold text-gray-800">{branch.teachers}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm text-gray-600">Task Completion</span>
                                                    <span className="text-sm font-semibold text-gray-800">{branch.attendance}%</span>
                                                </div>
                                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-green-500 rounded-full transition-all"
                                                        style={{ width: `${Math.min(branch.attendance, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm text-gray-600">Academic Performance</span>
                                                    <div className="flex items-center gap-1">
                                                        {getTrendIcon(branch.trend)}
                                                        <span className="text-sm font-semibold text-gray-800">{branch.performance}%</span>
                                                    </div>
                                                </div>
                                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-[#D67C2C] rounded-full transition-all"
                                                        style={{ width: `${Math.min(branch.performance, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                <BarChart3 className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <button
                                onClick={() => window.location.href = '/viewteachers'}
                                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all text-left group"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-100">
                                        <Users className="w-4 h-4 text-purple-600" />
                                    </div>
                                    <ChevronRight size={16} className="text-gray-400 group-hover:text-purple-500" />
                                </div>
                                <h4 className="font-medium text-gray-900">View Teachers</h4>
                                <p className="text-sm text-gray-500">Manage all educators</p>
                            </button>
                            <button
                                onClick={() => window.location.href = '/viewstudents'}
                                className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all text-left group"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100">
                                        <TrendingUp className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-500" />
                                </div>
                                <h4 className="font-medium text-gray-900">View Students</h4>
                                <p className="text-sm text-gray-500">Student management</p>
                            </button>
                            <button
                                onClick={() => window.location.href = '/report'}
                                className="p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all text-left group"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-100">
                                        <BarChart3 className="w-4 h-4 text-green-600" />
                                    </div>
                                    <ChevronRight size={16} className="text-gray-400 group-hover:text-green-500" />
                                </div>
                                <h4 className="font-medium text-gray-900">View Reports</h4>
                                <p className="text-sm text-gray-500">Student analytics</p>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ManagementDashboard;
